import React, { useState, useEffect } from "react";
import { useKeenSlider } from "keen-slider/react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import "keen-slider/keen-slider.min.css";

const esgData = [
    {
        id: 1,
        title: "Our environmental impact",
        image: "/assets/presence/img4.png",
        points: [
            "Be Carbon Neutral by 2040",
            "Be Water Positive by 2040",
            "Be Zero Waste by 2040"
        ]
    },
    {
        id: 2,
        title: "Social inclusiveness & community development",
        image: "/assets/presence/img5.png",
        points: [
            "Drive holistic well-being, health & wellness of the workforce",
            "Drive equality and inclusiveness at all levels",
            "Community development initiatives on education and environment"
        ]
    },
    {
        id: 3,
        title: "Strong ethics & code of conduct",
        image: "/assets/presence/img6.png",
        points: [
            "Adhere to strong code of ethics",
            "Secure digital assets with cybersecurity",
            "Empowered, diverse and inclusive board"
        ]
    },
];

const EsgCommitments = () => {
    const [currentSlide, setCurrentSlide] = useState(0);

    // ऑटोप्ले प्लगइन कॉन्फ़िगरेशन
    const [sliderRef, instanceRef] = useKeenSlider({
        initial: 0,
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        loop: true,
        slides: {
            perView: 1,
            spacing: 16,
        },
        breakpoints: {
            "screen and (min-width: 768px)": {
                slides: { perView: 2, spacing: 18 },
            },
        },
    }, [
        (slider) => {
            let timeout;
            let mouseOver = false;
            function clearNextTimeout() {
                clearTimeout(timeout);
            }
            function nextTimeout() {
                clearTimeout(timeout);
                if (mouseOver) return;
                timeout = setTimeout(() => {
                    slider.next();
                }, 3000); // 3 सेकंड में ऑटोप्ले
            }
            slider.on("created", () => {
                slider.container.addEventListener("mouseover", () => {
                    mouseOver = true;
                    clearNextTimeout();
                });
                slider.container.addEventListener("mouseout", () => {
                    mouseOver = false;
                    nextTimeout();
                });
                nextTimeout();
            });
            slider.on("animationEnded", nextTimeout);
            slider.on("updated", nextTimeout);
        },
    ]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 },
        },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
    };

    return (
        <section className="w-full bg-white pb-12 sm:pb-16 md:pb-20 lg:pb-24 text-left relative z-10 font-sans overflow-hidden">
            <div className="max-w-[1440px] mx-auto w-full px-5 sm:px-10 lg:px-[80px]">

                <div className="flex items-center justify-between mb-8 sm:mb-12 md:mb-16 w-full px-1">
                    <h2 className="text-[20px] md:text-[30px] font-[600] font-sora text-[#01386A] leading-tight tracking-tight">
                        Our ESG commitments
                    </h2>

                    <div className="flex items-center gap-3 shrink-0">
                        <button
                            onClick={() => instanceRef.current?.prev()}
                            className="w-10 h-10 rounded-full border border-[#ED6800] text-[#ED6800] flex items-center justify-center bg-transparent hover:bg-[#ED6800] hover:text-white transition-all duration-300 cursor-pointer outline-none select-none"
                            title="Previous item"
                        >
                            <ArrowLeft size={18} />
                        </button>
                        <button
                            onClick={() => instanceRef.current?.next()}
                            className="w-10 h-10 rounded-full border border-[#ED6800] text-[#ED6800] flex items-center justify-center bg-transparent hover:bg-[#ED6800] hover:text-white transition-all duration-300 cursor-pointer outline-none select-none"
                            title="Next item"
                        >
                            <ArrowRight size={18} />
                        </button>
                    </div>
                </div>

                <motion.div
                    ref={sliderRef}
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    className="keen-slider w-full items-stretch"
                >
                    {esgData.map((item) => (
                        <motion.div
                            key={item.id}
                            variants={cardVariants}
                            className="keen-slider__slide"
                        >
                            <div className="group relative w-full aspect-[16/10] sm:aspect-[16/9] md:aspect-square lg:aspect-[16/9] overflow-hidden bg-slate-900 shadow-sm flex items-end p-6 sm:p-10 md:p-12 min-h-full">
                                <div className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden">
                                    <motion.img
                                        whileHover={{ scale: 1.04 }}
                                        transition={{ duration: 0.6, ease: "easeOut" }}
                                        src={item.image}
                                        alt={item.title}
                                        className="w-full h-full object-cover absolute inset-0 transition-transform duration-500"
                                    />
                                </div>

                                <div className="relative z-20 w-full h-full flex flex-col justify-between gap-6 pointer-events-none">
                                    <h3 className="text-[16px] md:text-[20px] font-[600] font-sora text-white leading-tight tracking-tight break-words max-w-[500px]">
                                        {item.title}
                                    </h3>

                                    <ul className="space-y-2 w-full pl-1">
                                        {item.points.map((point, index) => (
                                            <li key={index} className="flex items-start gap-2.5 w-full text-[14px] sm:text-[16px] text-[#FFFFFF] font-graphik font-[500] lg:leading-[24px] leading-[20px] break-words">
                                                <span className="text-white text-[14px] leading-none pt-1 select-none">•</span>
                                                <span className="flex-1">{point}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

            </div>
        </section>
    );
};

export default EsgCommitments;